import { FC, useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Alert,
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  CircularProgress,
  Collapse,
  TextField,
  Typography,
} from "@mui/material";
import { AutocompleteRenderOptionWithHelpText } from "@benefit-systems/hr-tools-ui";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import { absencesAbsencesConfigurationWrite } from "src/utils/RoleBasedSecurity/permissions";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import __ from "src/utils/Translations";
import { useListAbsenceTypes } from "src/Pages/Company/Absences/api/absenceTypes.queries";
import {
  Hierarchy,
  IAbsenceType,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import { usePopulateForm } from "src/utils/hooks/absences/usePopulateForm";
import translateLabels from "src/utils/Translations/translateLabels";
import CustomRadioGroup from "src/Pages/Company/Absences/Forms/CustomRadioGroup";

const SelectParentAbsenceType: FC<{
  absenceTypeId?: string;
  fullyEditable?: boolean;
  initialParentAbsenceTypeId?: string | null;
}> = ({ absenceTypeId, fullyEditable, initialParentAbsenceTypeId }) => {
  const canWriteAbsencesConfiguration = usePermission(
    absencesAbsencesConfigurationWrite
  );
  // Absence Type is editable is when it's new (undefined) or has enabled flag
  const isEditable = fullyEditable === true || fullyEditable === undefined;

  const { control } = useFormContext();
  const { populateAbsenceTypeFromParent } = usePopulateForm();

  const chosenHierarchy = useWatch({ name: "hierarchy" });
  const chosenParentAbsenceType = useWatch({
    name: "billingCycleConfiguration.parentAbsenceTypeId",
  });

  const visible = chosenHierarchy === Hierarchy.CHILD;

  const companyId = useCompanyId();
  const { data: absenceTypes, isFetching } = useListAbsenceTypes(companyId);

  const hierarchyOptions = useMemo(
    () => [
      { label: "Szablon główny", value: Hierarchy.MAIN },
      {
        label: "Szablon podrzędny",
        value: Hierarchy.CHILD,
      },
    ],
    []
  );

  const getHelpText = useCallback(
    (item: IAbsenceType) => {
      const text = [];
      if (item.isMain) {
        text.push(__("Oznaczono jako Urlop Wypoczynkowy."));
      }
      if (item.billingCycleConfiguration?.parentAbsenceTypeId) {
        const parentName = absenceTypes?.find(
          (o) => o.id === item.billingCycleConfiguration?.parentAbsenceTypeId
        )?.name;
        text.push(`${__("Wymiar podrzędny dla")} ${parentName}.`);
      }
      return text.join(" ");
    },
    [absenceTypes]
  );

  const prepareOption = useCallback(
    (item: IAbsenceType) => ({
      value: item.id,
      label: item.name,
      helpText: getHelpText(item),
    }),
    [getHelpText]
  );

  const absenceTypesOptions = useMemo(
    () =>
      absenceTypes
        ?.filter(
          (o) =>
            o.id !== absenceTypeId &&
            o.active &&
            o.billingCycleConfiguration &&
            !o.billingCycleConfiguration?.parentAbsenceTypeId
        )
        .map((o) => prepareOption(o)),
    [absenceTypeId, absenceTypes, prepareOption]
  );

  const selectedAbsenceType = useCallback(
    (value: string) => {
      const selected = absenceTypes?.find((o) => o.id === value);
      return selected ? prepareOption(selected) : null;
    },
    [absenceTypes, prepareOption]
  );

  useEffect(() => {
    const parentSameAsInitial =
      initialParentAbsenceTypeId === chosenParentAbsenceType;

    if (chosenParentAbsenceType && !parentSameAsInitial) {
      const parentAbsenceTypeData = absenceTypes?.find(
        (absenceType) => absenceType.id === chosenParentAbsenceType
      );

      if (parentAbsenceTypeData) {
        // Populate some of the fields with the parent absence type data
        populateAbsenceTypeFromParent(parentAbsenceTypeData);
      }
    }
  }, [
    absenceTypes,
    chosenParentAbsenceType,
    initialParentAbsenceTypeId,
    populateAbsenceTypeFromParent,
  ]);

  return (
    <>
      <CustomRadioGroup
        id="hierarchy"
        name="hierarchy"
        label={__("Powiązanie z innym szablonem")}
        options={translateLabels(hierarchyOptions)}
        disabled={!canWriteAbsencesConfiguration || !isEditable}
        dataTestId="absence-type-form-field__hierarchy"
      />
      <Collapse in={visible}>
        <Box
          sx={{
            bgcolor: "grey.100",
            padding: "1rem 2rem",
            marginTop: "1rem",
            borderRadius: "0.5rem",
          }}
        >
          <Typography variant="subtitle1">
            {__("Wybór szablonu głównego")}
          </Typography>
          <Typography>
            {__(
              "Wymiar szablonu podrzędnego będzie zawarty w wymiarze wybranego szablonu głównego."
            )}
          </Typography>
          <Controller
            name="billingCycleConfiguration.parentAbsenceTypeId"
            control={control}
            render={({
              field: { value, onChange, ref },
              fieldState: { invalid, error },
            }) => (
              <Autocomplete
                fullWidth
                disablePortal
                options={absenceTypesOptions || []}
                value={selectedAbsenceType(value)}
                loading={isFetching}
                onChange={(_event, newValue) => onChange(newValue?.value)}
                renderOption={AutocompleteRenderOptionWithHelpText}
                disabled={!canWriteAbsencesConfiguration || !isEditable}
                renderInput={(params: AutocompleteRenderInputParams) => (
                  <TextField
                    {...params}
                    label={__("Wskaż wniosek")}
                    margin="normal"
                    error={invalid}
                    helperText={error?.message}
                    inputRef={ref}
                    slotProps={{
                      ...params.slotProps,
                      input: {
                        ...params.slotProps?.input,
                        endAdornment: (
                          <>
                            {isFetching ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {(params.slotProps?.input as any)?.endAdornment}
                          </>
                        ),
                      },
                    }}
                    data-testid="absence-type-form-field__parent-absence-type"
                  />
                )}
              />
            )}
          />
          <Alert severity="info">
            {__(
              "Pola poniżej zostaną automatycznie uzupełnione zgodnie z szablonem głównym. W części pól zmiana ustawień nie będzie możliwa."
            )}
          </Alert>
        </Box>
      </Collapse>
    </>
  );
};

export default SelectParentAbsenceType;
